/**
 * File: dateRangeUtils.ts
 * Path: apps/admin/src/shared/utils/dateRangeUtils.ts
 * Description: Calendar helpers and the date-range check behind the Overview's
 * date range picker.
 */
import { shortDate, yearBefore, type DateRange } from "./overviewFiguresUtils";

export const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
export const WEEKDAYS = ["M", "T", "W", "T", "F", "S", "S"];

const pad = (n: number) => String(n).padStart(2, "0");

export const sameMonth = (day: string) => day.slice(0, 7);

export const monthShift = (month: string, by: number) => {
  const d = new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)) - 1 + by, 1));
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}`;
};

/** The weeks of a month as rows of seven days (Monday first), with null for the blanks. */
export const weeksOf = (month: string): (string | null)[][] => {
  const first = new Date(`${month}-01T00:00:00Z`);
  const lead = (first.getUTCDay() + 6) % 7;
  const days = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate();
  const cells: (string | null)[] = [...Array<null>(lead).fill(null), ...Array.from({ length: days }, (_, i) => `${month}-${pad(i + 1)}`)];
  while (cells.length % 7 !== 0) cells.push(null);
  return Array.from({ length: cells.length / 7 }, (_, w) => cells.slice(w * 7, w * 7 + 7));
};

export const dayMonth = (day: string) => shortDate(day).slice(0, -5);

/** Why a picked range can't be applied, or null when it can. */
export const rangeProblem = (draft: DateRange, today: string, booksStart: string): string | null =>
  !draft.from || !draft.to
    ? "Pick both dates."
    : draft.from > draft.to
      ? "The start must be before the end."
      : draft.to > today
        ? "The end can't be in the future."
        : draft.from < booksStart
          ? `The books start on ${shortDate(booksStart)}.`
          : null;

/** The same dates a year earlier, as shown under the calendar. */
export const comparisonText = (draft: DateRange): string => `${dayMonth(yearBefore(draft.from))} – ${shortDate(yearBefore(draft.to))}`;
